import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { z } from "zod/v3";
import type { Path } from "@/router";
import DateInput from "@/components/DateInput";
import DetailsCard from "@/components/DetailsCard";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import MultiStepForm from "@/components/MultiStepForm";
import ProcessStepLineItem from "@/components/ProcessStepLineItem";
import Select from "@/components/Select";
import MultiStepFormSkeleton from "@/components/Skeleton/MultiStepFormSkeleton";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { toFormat } from "@/lib/dateHelpers";
import { useNavigate, useParams } from "@/router";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { zodV3Resolver } from "@/lib/zodV3Resolver";
import { COMMISSION_CAMPAIGNS_QUERY } from "@/pages/billing/finance/commission/campaigns/queries";
import type { CommissionCampaignNode } from "@/pages/billing/finance/commission/campaigns/types";
import { CREATE_COMMISSION_PROGRAM_CONSENT_MUTATION } from "@/pages/billing/finance/commission/consents/mutations";
import { COMMISSION_PROGRAM_CONSENTS_QUERY } from "@/pages/billing/finance/commission/consents/queries";
import type {
  CommissionAttributionModel,
  CommissionConsentOwnership
} from "@/pages/billing/finance/commission/consents/types";
import { formatEnumLabel } from "@/pages/billing/finance/commission/_utils";
import {
  useCanShowCommissionPlatformBool,
  useRequireCommissionPlatform
} from "@/pages/billing/finance/commission/useCommissionPlatformGate";
import type { StepStatus } from "@/providers/ProcessTrackerProvider";
import { GrpCampaignMultiSelectFields, sponsorHeadline } from "../_components/PersonGrpFormChrome";
import { currentGrpProgramYear, GRP_COMMISSION_CAMPAIGNS_LIMIT } from "../constants";
import { PERSON_GRP_CONTEXT_QUERY } from "../queries";

const attributionOptions: CommissionAttributionModel[] = ["FACILITY", "PERSONAL"];
const ownershipOptions: CommissionConsentOwnership[] = ["OWNER", "NON_OWNER"];

const createFaaSchema = z
  .object({
    selectedCampaignIds: z.array(z.string()).min(1, "Select at least one sponsor."),
    attributionModel: z.enum(["FACILITY", "PERSONAL"]),
    ownership: z.enum(["OWNER", "NON_OWNER"]),
    startsAt: z.string().min(1, "Effective date is required."),
    endsAt: z.string(),
    status: z.enum(["ACTIVE", "ENDED", "REVOKED"])
  })
  .superRefine((values, ctx) => {
    if (values.startsAt && values.endsAt && values.endsAt < values.startsAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endsAt"],
        message: "Expiration date must be on or after the effective date."
      });
    }
  });

type CreateFaaFormValues = z.infer<typeof createFaaSchema>;

function campaignHeadline(campaigns: CommissionCampaignNode[], campaignId: string) {
  const campaign = campaigns.find((node) => node.id === campaignId);
  if (!campaign) return campaignId;
  return sponsorHeadline(campaign);
}

export default function CreatePersonGrpFaaPage() {
  const { pgaId = "" } = useParams("/people/:pgaId/benefits/grp/faa/new");
  const navigate = useNavigate();
  const client = useApolloClient();
  const grpPath = `/people/${pgaId}/benefits/grp` as Path;
  usePageTitle("/people/:pgaId/benefits/grp/faa/new", "New Facility Authorization Agreement");
  useRequireCapability({ domain: DOMAIN.BENEFIT_PROGRAMS, permission: PERMISSION.manageGrpCommission });
  useRequireCommissionPlatform(grpPath);
  const canUseCommissionPlatform = useCanShowCommissionPlatformBool();

  const {
    data: personData,
    loading: personLoading,
    error: personError
  } = useQuery(PERSON_GRP_CONTEXT_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !canUseCommissionPlatform
  });
  const {
    data: campaignsData,
    loading: campaignsLoading,
    error: campaignsError
  } = useQuery(COMMISSION_CAMPAIGNS_QUERY, {
    variables: { first: GRP_COMMISSION_CAMPAIGNS_LIMIT, editionYear: currentGrpProgramYear() },
    skip: !canUseCommissionPlatform
  });
  const [createConsent] = useMutation(CREATE_COMMISSION_PROGRAM_CONSENT_MUTATION);

  const campaigns = campaignsData?.commissionCampaigns.nodes ?? [];
  const memberName = personData?.person.displayName ?? "this member";
  const crmPersonId = personData?.person.id;

  // The backend allows one consent per person and campaign, so sponsors this member
  // already has an FAA with are left out of the picker. Consents are only queryable per campaign.
  const [existingFaaCampaignIds, setExistingFaaCampaignIds] = useState<Set<string> | null>(null);
  const [existingFaasError, setExistingFaasError] = useState<Error | null>(null);

  useEffect(() => {
    if (!crmPersonId || !campaignsData) return;

    let cancelled = false;
    // Settle every lookup: a failed one leaves that sponsor in the picker (the backend
    // still rejects a duplicate on save); only a total failure blocks the form.
    Promise.allSettled(
      campaignsData.commissionCampaigns.nodes.map((campaign) =>
        client
          .query({
            query: COMMISSION_PROGRAM_CONSENTS_QUERY,
            variables: { campaignId: campaign.id, crmPersonId, first: 1 },
            fetchPolicy: "network-only"
          })
          .then((result) => ((result.data?.commissionProgramConsents.nodes.length ?? 0) > 0 ? campaign.id : null))
      )
    ).then((results) => {
      if (cancelled) return;
      const failed = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      if (failed && results.every((result) => result.status === "rejected")) {
        setExistingFaasError(
          failed.reason instanceof Error ? failed.reason : new Error("Failed to load existing FAAs.")
        );
        return;
      }
      setExistingFaaCampaignIds(
        new Set(
          results.flatMap((result) => (result.status === "fulfilled" && result.value != null ? [result.value] : []))
        )
      );
    });

    return () => {
      cancelled = true;
    };
  }, [campaignsData, client, crmPersonId]);

  const form = useForm<CreateFaaFormValues>({
    resolver: zodV3Resolver(createFaaSchema),
    defaultValues: {
      selectedCampaignIds: [],
      attributionModel: "FACILITY",
      ownership: "OWNER",
      startsAt: "",
      endsAt: "",
      status: "ACTIVE"
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveProgress, setSaveProgress] = useState<Record<string, StepStatus>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const watchedValues = form.watch();
  const selectedCampaignIds = watchedValues.selectedCampaignIds;

  // Step validation runs on Next, not submit, so react-hook-form never re-validates on
  // change; clear stale errors as soon as the staff member edits an errored field.
  const revalidateIfErrored = (names: (keyof CreateFaaFormValues)[]) => {
    if (names.some((name) => form.getFieldState(name).error)) {
      void form.trigger(names);
    }
  };

  const onSubmit = async (values: CreateFaaFormValues) => {
    const crmPersonId = personData?.person.id;
    if (!crmPersonId) {
      showErrorSnackbar("Person record is not available.");
      return;
    }

    const campaignIds = values.selectedCampaignIds;
    // A retry after a partial save must not re-send sponsors that already saved;
    // the backend allows one consent per person and campaign.
    const pendingIds = campaignIds.filter((campaignId) => saveProgress[campaignId] !== "complete");
    setIsSubmitting(true);
    setSaveErrors({});
    setSaveProgress((prev) =>
      campaignIds.reduce<Record<string, StepStatus>>(
        (acc, campaignId) => {
          if (acc[campaignId] !== "complete") acc[campaignId] = "pending";
          return acc;
        },
        Object.fromEntries(Object.entries(prev).filter(([, status]) => status === "complete"))
      )
    );

    let hadError = false;
    let createdCount = 0;

    for (const commissionCampaignId of pendingIds) {
      setSaveProgress((prev) => ({ ...prev, [commissionCampaignId]: "processing" }));
      try {
        await createConsent({
          variables: {
            input: {
              commissionCampaignId,
              crmPersonId,
              attributionModel: values.attributionModel,
              ownership: values.ownership,
              startsAt: `${values.startsAt}T00:00:00Z`,
              endsAt: values.endsAt ? `${values.endsAt}T23:59:59Z` : null,
              status: values.status
            }
          }
        });
        createdCount += 1;
        setSaveProgress((prev) => ({ ...prev, [commissionCampaignId]: "complete" }));
      } catch (error) {
        hadError = true;
        setSaveProgress((prev) => ({ ...prev, [commissionCampaignId]: "error" }));
        if (error instanceof Error && error.message) {
          setSaveErrors((prev) => ({ ...prev, [commissionCampaignId]: error.message }));
        }
      }
    }

    if (createdCount > 0) {
      // The GRP tab reads consents with client.query (cache-first); drop cached
      // lists so the new FAA rows load on return.
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "commissionProgramConsents" });
      client.cache.gc();
    }

    setIsSubmitting(false);

    if (hadError) {
      if (createdCount > 0) {
        // Saved sponsors are not unsaved by Cancel, so leaving now should not prompt a discard.
        form.reset(values);
      }
      showErrorSnackbar("Unable to save FAA for every selected sponsor. Saved sponsors won't be sent again.");
      return;
    }

    showSnackbar(campaignIds.length === 1 ? "FAA created." : `FAA created for ${campaignIds.length} sponsors.`);
    navigate("/people/:pgaId/benefits/grp", { params: { pgaId } });
  };

  // Once any sponsor has saved, lock the details and the saved sponsors so a retry
  // sends the same FAA terms; sponsors that failed can still be dropped.
  const savedCampaignIds = Object.keys(saveProgress).filter((campaignId) => saveProgress[campaignId] === "complete");
  const fieldsLocked = isSubmitting || savedCampaignIds.length > 0;

  const loadError = personError ?? campaignsError ?? existingFaasError;
  const selectableCampaigns = campaigns.filter(
    (campaign) =>
      savedCampaignIds.includes(campaign.id) ||
      (campaign.status !== "CLOSED" && !existingFaaCampaignIds?.has(campaign.id))
  );
  const progressEntries = Object.entries(saveProgress).filter(([campaignId]) =>
    selectedCampaignIds.includes(campaignId)
  );

  const renderCampaignStep = () => {
    if (loadError) {
      return <ErrorBoundary variant="inline" title="Failed to Load FAA Form" message={loadError.message} />;
    }
    return (
      <Controller
        name="selectedCampaignIds"
        control={form.control}
        render={({ field, fieldState }) => (
          <GrpCampaignMultiSelectFields
            campaigns={selectableCampaigns}
            selectedIds={field.value}
            onChange={(nextIds) => {
              field.onChange(nextIds);
              revalidateIfErrored(["selectedCampaignIds"]);
              if (savedCampaignIds.length > 0) {
                // Saved sponsors stay saved, so dropping a failing one must not make Cancel prompt a discard.
                form.reset(form.getValues());
              }
            }}
            disabled={isSubmitting}
            lockedIds={savedCampaignIds}
            errorMessage={fieldState.error?.message}
          />
        )}
      />
    );
  };

  const steps = [
    {
      headline: "Authorized Sponsors",
      supportingText: selectedCampaignIds.length ? `${selectedCampaignIds.length} selected` : "Choose sponsors",
      requiredFields: ["selectedCampaignIds"],
      content: renderCampaignStep()
    },
    {
      headline: "FAA Details",
      supportingText: "Plan option and effective dates",
      requiredFields: ["attributionModel", "ownership", "startsAt", "endsAt"],
      content: (
        <div className="flex flex-col gap-6">
          <h2 className="text-headline-small">Enter the plan option and effective dates.</h2>

          <Controller
            name="ownership"
            control={form.control}
            render={({ field }) => (
              <Select
                label="Plan Option"
                value={field.value}
                onSelect={field.onChange}
                options={ownershipOptions.map((value) => ({ value, label: formatEnumLabel(value) }))}
                disabled={fieldsLocked}
              />
            )}
          />

          <Controller
            name="attributionModel"
            control={form.control}
            render={({ field }) => (
              <Select
                label="Attribution"
                value={field.value}
                onSelect={field.onChange}
                options={attributionOptions.map((value) => ({ value, label: formatEnumLabel(value) }))}
                disabled={fieldsLocked}
              />
            )}
          />

          <Controller
            name="startsAt"
            control={form.control}
            render={({ field, fieldState }) => (
              <DateInput
                label="Effective Date"
                value={field.value || null}
                onChange={(next) => {
                  field.onChange(next ?? "");
                  revalidateIfErrored(["startsAt", "endsAt"]);
                }}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                supportingText="Date the FAA takes effect."
                disabled={fieldsLocked}
              />
            )}
          />

          <Controller
            name="endsAt"
            control={form.control}
            render={({ field, fieldState }) => (
              <DateInput
                label="Expiration Date"
                value={field.value || null}
                onChange={(next) => {
                  field.onChange(next ?? "");
                  revalidateIfErrored(["startsAt", "endsAt"]);
                }}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                supportingText="Optional. Leave blank for an open-ended FAA."
                disabled={fieldsLocked}
              />
            )}
          />
        </div>
      )
    },
    {
      headline: "Review",
      supportingText: "Confirm and activate",
      content: (
        <div className="flex flex-col gap-6">
          <h2 className="text-headline-small">Review the FAA before activating it.</h2>
          <DetailsCard
            testId="faa-review"
            options={[
              { overline: "Member", headline: memberName, iconName: "person" },
              {
                overline: "Authorized Sponsors",
                headline: selectedCampaignIds.map((id) => campaignHeadline(campaigns, id)).join("\n") || "—",
                iconName: "handshake",
                longContent: true
              },
              { overline: "Plan Option", headline: formatEnumLabel(watchedValues.ownership), iconName: "badge" },
              {
                overline: "Attribution",
                headline: formatEnumLabel(watchedValues.attributionModel),
                iconName: "account_tree"
              },
              {
                overline: "Effective Date",
                headline: watchedValues.startsAt ? toFormat(watchedValues.startsAt) : "—",
                iconName: "today"
              },
              {
                overline: "Expiration Date",
                headline: watchedValues.endsAt ? toFormat(watchedValues.endsAt) : "—",
                iconName: "event"
              }
            ]}
          />

          {progressEntries.length > 0 && (
            <div className="flex flex-col gap-3">
              {progressEntries.map(([campaignId, status]) => (
                <ProcessStepLineItem
                  key={campaignId}
                  bordered
                  testId={`faa-save-${campaignId}`}
                  status={status}
                  headline={campaignHeadline(campaigns, campaignId)}
                  supportingText={
                    status === "error"
                      ? (saveErrors[campaignId] ?? "Unable to create FAA for this sponsor.")
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )
    }
  ];

  if (
    !canUseCommissionPlatform ||
    personLoading ||
    campaignsLoading ||
    (crmPersonId && campaignsData && !existingFaaCampaignIds && !existingFaasError)
  ) {
    return <MultiStepFormSkeleton stepCount={steps.length} renderStepper />;
  }

  if (!loadError && selectableCampaigns.length === 0) {
    return (
      <FocusedPageLayout backLink={{ to: "/people/:pgaId/benefits/grp", params: { pgaId } }}>
        <EmptyState
          title={
            campaigns.some((campaign) => campaign.status !== "CLOSED")
              ? "All Available Sponsors Added"
              : "No Sponsors Available"
          }
          caption="Set up a sponsor in the Commission Platform before recording an FAA."
          iconName="handshake"
          action={{
            to: "/billing/finance/commission",
            children: "Commission Platform"
          }}
        />
      </FocusedPageLayout>
    );
  }

  return (
    <MultiStepForm
      form={form}
      steps={steps}
      renderStepper
      showActions
      validatePathOnStepClick
      disableSubmitUntilLastStep
      modelName="FAA"
      isEditing
      submitText="Activate"
      submittingText="Saving..."
      isSubmitting={isSubmitting}
      disabledSubmit={!personData?.person.id}
      backLink={{ to: grpPath, title: "Cancel" }}
      onCancel={() => navigate("/people/:pgaId/benefits/grp", { params: { pgaId } })}
      onSubmit={onSubmit}
    />
  );
}
